import React, { useState, useEffect, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';

interface Coordinates {
  address: string;
  lat: number;
  lon: number;
}

interface Suggestion {
  display_name: string;
  place_id: string;
}

interface AddressComponentProps {
  onSelect: (coordinates: Coordinates | null) => void;
  debounceDelay?: number;
}

const libraries: 'places'[] = ['places'];

const AddressComponent: React.FC<AddressComponentProps> = ({
  onSelect,
  debounceDelay = 500,
}) => {
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showPureAddr, setShowPureAddr] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
    libraries,
  });

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
    }

    if (isLoaded && !placesService.current) {
      placesService.current = new google.maps.places.PlacesService(
        document.createElement('div')
      );
    }

    if (isLoaded && !sessionToken.current) {
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  // Handle clicks outside the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions when address changes
  useEffect(() => {
    if (!isLoaded || !autocompleteService.current) return;

    if (address.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      setIsLoading(true);
      setError(null);

      autocompleteService.current?.getPlacePredictions(
        {
          input: address,
          sessionToken: sessionToken.current || undefined,
        },
        (predictions, status) => {
          setIsLoading(false);
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            const formattedSuggestions = predictions.map((p) => ({
              display_name: p.description,
              place_id: p.place_id,
            }));
            setSuggestions(formattedSuggestions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [address, debounceDelay, isLoaded]);

  useEffect(() => {
    if (showPureAddr) {
      const coordinates = {
        address: address,
        lat: 0,
        lon: 0,
      };
      onSelect(coordinates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPureAddr, address]);

  const getComponent = (
    components: google.maps.GeocoderAddressComponent[],
    type: string
  ) =>
    components.find((component) => component.types.includes(type))?.long_name ||
    '';

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    setAddress(suggestion.display_name);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowPureAddr(false);

    if (!placesService.current) return;

    setIsLoading(true);

    placesService.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: [
          'formatted_address',
          'address_components',
          'geometry',
          'place_id',
        ],
        sessionToken: sessionToken.current || undefined,
      },
      (place, status) => {
        setIsLoading(false);

        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place?.geometry?.location
        ) {
          const lat = place.geometry.location.lat();
          const lon = place.geometry.location.lng();
          const components = place.address_components || [];
          const addressParts = [
            getComponent(components, 'street_number'),
            getComponent(components, 'route'),
            getComponent(components, 'locality'),
            getComponent(components, 'administrative_area_level_1'),
            getComponent(components, 'postal_code'),
            getComponent(components, 'country'),
          ].filter(Boolean);
          const composedAddress = addressParts.join(', ');
          const coordinates = {
            address:
              composedAddress ||
              place.formatted_address ||
              suggestion.display_name,
            lat,
            lon,
          };
          onSelect(coordinates);
          sessionToken.current =
            new google.maps.places.AutocompleteSessionToken();
        } else {
          const coordinates = {
            address: suggestion.display_name,
            lat: 0,
            lon: 0,
          };
          onSelect(coordinates);
        }
      }
    );
  };

  if (loadError) {
    return (
      <div className="text-red-500 text-sm mt-2">
        Error loading Google Maps API
      </div>
    );
  }

  return (
    <div className="mx-auto " ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            setShowPureAddr(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Enter an address (min 3 characters)"
          className="px-4
          h-[40px] w-full rounded bg-white text-gray-800 focus:border-none focus:outline-none focus:ring-0 active:border-none disabled:bg-gray-100"
          aria-autocomplete="list"
          aria-controls="suggestions-list"
          disabled={!isLoaded}
        />
        {isLoading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
          </div>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <ul
            id="suggestions-list"
            className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg mt-1 max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.place_id}
                className="text-primary-600 p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion.display_name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
    </div>
  );
};

export default AddressComponent;
